// Generador PowerPoint del Informe Gerencial — estructura ESPEJO del deck del
// cliente (mockup aprobado gen-informe-pptx.js, portado a TS/browser con datos
// reales). NO sigue la estructura del PDF: portada co-branding, facturación,
// nivel de servicio, espacios reservados RRHH (opcionales), productividad por
// área × mes, líderes por línea, paradas por línea × mes, costos y cierre.
//
// pptxgenjs se carga con import() dinámico SOLO al generar (no engorda el
// bundle inicial). Patrones anti-corrupción heredados del mockup:
//   - Barras APILADAS: dataLabelPosition 'ctr' (nunca 'outEnd' en stacked).
//   - Totales de apilado: serie de LÍNEA blanca (invisible) con labels 't'.
//   - Combos con secondaryValAxis: SIEMPRE valAxes + catAxes con 2 entradas.
//   - Doughnut: chartColors por punto.
//   - Tablas nativas con celdas coloreadas por semáforo 90/70.
//   - Fuentes Cambria (títulos) / Calibri (cuerpo). Colores hex SIN '#'.

import type PptxGenJS from 'pptxgenjs';
import { filasConOtras } from './informeGerencialDatos';
import type {
  PptxInput,
  PptxLideresLinea,
  PptxNivel,
  PptxParosCelda,
  PptxProductividadCelda,
} from './informeGerencialPptxDatos';

// ── Paleta JIDOKAP (UI light) — hex SIN '#', como exige pptxgenjs ────────────
const C = {
  fg: '211D1A',
  fg2: '5C564F',
  fg3: '8D867D',
  accent: '9C2020',
  ok: '1E7A46',
  crit: 'B3261E',
  warn: 'A97B12',
  border: 'E7E2DA',
  borderSoft: 'F0ECE5',
  bgSoft: 'FAF8F5',
  white: 'FFFFFF',
  azul: '4A78B4',
  azulSoft: 'A9C0DC',
  beige: 'D8B28F',
} as const;
const F_TITLE = 'Cambria';
const F_BODY = 'Calibri';
const W = 13.33;
const H = 7.5;

/** Colores rotativos para series por área/categoría. */
const PALETA_SERIES = [C.accent, C.azul, C.fg3, C.beige, C.warn, C.ok, C.azulSoft];

// ── Formatters es-CO ─────────────────────────────────────────────────────────
const fmtMiles = (n: number): string => Math.round(n).toLocaleString('es-CO');
const fmtM = (n: number): string =>
  `$ ${n.toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
const fmtPct = (n: number | null): string => (n === null ? '—' : `${Math.round(n)}%`);
/** Trunca con elipsis para que un texto largo no envuelva la fila de la tabla. */
const trunca = (t: string, max: number): string => (t.length > max ? `${t.slice(0, max - 1)}…` : t);
const fmtTiempo = (min: number): string => {
  const m = Math.round(min);
  return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m` : `${m} m`;
};
const semColor = (p: number | null): string =>
  p === null ? C.fg3 : p >= 90 ? C.ok : p >= 70 ? C.warn : C.crit;
/** "Junio 2026" → "Jun 26" (eje de meses). */
const mesCorto = (label: string): string => {
  const [mes, anio] = label.split(' ');
  return `${(mes ?? '').slice(0, 3)} ${(anio ?? '').slice(2)}`;
};

type Slide = PptxGenJS.Slide;
/** Cast utilitario: las opciones de chart de pptxgenjs tipan solo un subset. */
const co = (o: object): PptxGenJS.IChartOpts => o as PptxGenJS.IChartOpts;

/**
 * Combo multi-tipo: el runtime acepta addChart(chartsArray, opciones) — con
 * primer arg array, el segundo ES las opciones (`tmpOpt = data || opt` en el
 * código de pptxgenjs) — pero el .d.ts solo tipa (type, data, options).
 */
function addComboChart(s: Slide, charts: object[], opts: object): void {
  (s.addChart as unknown as (t: object[], o: object) => void)(charts, opts);
}

// =============================================================================
// Contexto de construcción (numeración y helpers con estado)
// =============================================================================

interface Ctx {
  pres: PptxGenJS;
  input: PptxInput;
  total: number;
  pageNum: number;
  onProgreso?: (msg: string) => void;
}

function nuevaSlide(ctx: Ctx): Slide {
  ctx.pageNum++;
  ctx.onProgreso?.(`Preparando slide ${ctx.pageNum} de ${ctx.total}…`);
  const s = ctx.pres.addSlide();
  s.background = { color: C.white };
  return s;
}

function header(ctx: Ctx, s: Slide, titulo: string, subtitulo?: string): void {
  s.addText(titulo, {
    x: 0.55,
    y: 0.28,
    w: 9.6,
    h: 0.62,
    fontFace: F_TITLE,
    fontSize: 27,
    bold: true,
    color: C.fg,
    margin: 0,
  });
  if (subtitulo) {
    s.addText(subtitulo.toUpperCase(), {
      x: 0.57,
      y: 0.88,
      w: 9.6,
      h: 0.3,
      fontFace: F_BODY,
      fontSize: 10.5,
      color: C.fg3,
      charSpacing: 2,
      margin: 0,
    });
  }
  s.addText(
    [
      { text: 'Jidokap', options: { fontFace: F_TITLE, color: C.fg } },
      { text: '.', options: { fontFace: F_TITLE, color: C.accent } },
      { text: '  ×  ', options: { fontFace: F_BODY, color: C.fg3, fontSize: 11 } },
      {
        text: ctx.input.tenantNombre,
        options: { fontFace: F_BODY, color: C.fg2, bold: true, fontSize: 12.5 },
      },
    ],
    { x: W - 3.6, y: 0.32, w: 3.05, h: 0.4, fontSize: 15, align: 'right', margin: 0 },
  );
  s.addShape('line', { x: 0.55, y: 1.24, w: W - 1.1, h: 0, line: { color: C.border, width: 1 } });
}

function footer(ctx: Ctx, s: Slide, fuenteJidokap = true): void {
  const izq = fuenteJidokap
    ? '✓ Solo información validada · generado desde JIDOKAP'
    : 'Fuente externa a JIDOKAP';
  s.addText(izq, {
    x: 0.55,
    y: H - 0.42,
    w: 6,
    h: 0.3,
    fontFace: F_BODY,
    fontSize: 8.5,
    color: fuenteJidokap ? C.ok : C.fg3,
    margin: 0,
  });
  s.addText(`Informe gerencial · ${ctx.input.periodoLabel} · ${ctx.pageNum} / ${ctx.total}`, {
    x: W - 5.55,
    y: H - 0.42,
    w: 5,
    h: 0.3,
    fontFace: F_BODY,
    fontSize: 8.5,
    color: C.fg3,
    align: 'right',
    margin: 0,
  });
}

function card(s: Slide, x: number, y: number, w: number, h: number): void {
  s.addShape('roundRect', {
    x,
    y,
    w,
    h,
    rectRadius: 0.06,
    fill: { color: C.white },
    line: { color: C.border, width: 1 },
  });
}

function cardTitulo(s: Slide, x: number, y: number, texto: string): void {
  s.addText(texto.toUpperCase(), {
    x: x + 0.18,
    // Nunca salirse del canvas: los cards de la derecha son más angostos que 6".
    y: y + 0.12,
    w: Math.min(6, W - (x + 0.18) - 0.35),
    h: 0.28,
    fontFace: F_BODY,
    fontSize: 10,
    color: C.fg3,
    charSpacing: 1.6,
    bold: true,
    margin: 0,
  });
}

function kpiCard(
  s: Slide,
  x: number,
  y: number,
  w: number,
  lbl: string,
  val: string,
  sub: string,
  colorVal?: string,
): void {
  card(s, x, y, w, 1.28);
  s.addText(lbl.toUpperCase(), {
    x: x + 0.16,
    y: y + 0.11,
    w: w - 0.3,
    h: 0.24,
    fontFace: F_BODY,
    fontSize: 9,
    color: C.fg3,
    charSpacing: 1.4,
    margin: 0,
  });
  s.addText(val, {
    x: x + 0.16,
    y: y + 0.34,
    w: w - 0.3,
    h: 0.52,
    fontFace: F_BODY,
    fontSize: 25,
    bold: true,
    color: colorVal ?? C.fg,
    margin: 0,
  });
  s.addText(sub, {
    x: x + 0.16,
    y: y + 0.9,
    w: w - 0.3,
    h: 0.26,
    fontFace: F_BODY,
    fontSize: 9.5,
    color: C.fg2,
    margin: 0,
  });
}

/** Caja punteada de estado vacío (también la usan los espacios RRHH). */
function cajaVacia(s: Slide, titulo: string, detalle: string): void {
  s.addShape('roundRect', {
    x: 0.55,
    y: 1.6,
    w: W - 1.1,
    h: 5.15,
    rectRadius: 0.08,
    fill: { color: C.bgSoft },
    line: { color: C.fg3, width: 1, dashType: 'dash' },
  });
  s.addText(titulo, {
    x: 0.55,
    y: 3.3,
    w: W - 1.1,
    h: 0.45,
    align: 'center',
    fontFace: F_BODY,
    fontSize: 15,
    bold: true,
    color: C.fg3,
    charSpacing: 3,
    margin: 0,
  });
  s.addText(detalle, {
    x: 0.55,
    y: 3.85,
    w: W - 1.1,
    h: 0.7,
    align: 'center',
    fontFace: F_BODY,
    fontSize: 12,
    color: C.fg2,
    margin: 0,
  });
}

// ── Celdas de tabla ──────────────────────────────────────────────────────────
type Cell = PptxGenJS.TableCell;
const cel = (t: string | number, opts: object = {}): Cell => ({
  text: String(t),
  options: { fontFace: F_BODY, align: 'right', ...opts } as Cell['options'],
});
const enc = (t: string, align: 'left' | 'right' = 'right'): Cell =>
  cel(t, { align, color: C.fg3, bold: true, fill: { color: C.bgSoft } });

// =============================================================================
// Slides
// =============================================================================

function slidePortada(ctx: Ctx): void {
  const { input } = ctx;
  const s = nuevaSlide(ctx);
  s.background = { color: C.bgSoft };
  s.addText(
    [
      { text: 'Jidokap', options: { fontFace: F_TITLE, color: C.fg } },
      { text: '.', options: { fontFace: F_TITLE, color: C.accent } },
      { text: '  ×  ', options: { fontFace: F_BODY, color: C.fg3, fontSize: 16 } },
      {
        text: input.tenantNombre,
        options: { fontFace: F_BODY, color: C.fg2, bold: true, fontSize: 18 },
      },
    ],
    { x: 0.75, y: 0.6, w: 8, h: 0.5, fontSize: 22, margin: 0 },
  );
  s.addText('Informe gerencial de producción', {
    x: 0.72,
    y: 2.3,
    w: 11.6,
    h: 1.15,
    fontFace: F_TITLE,
    fontSize: 47,
    bold: true,
    color: C.fg,
    margin: 0,
  });
  s.addText(`${input.tenantNombre} · ${input.plantaLabel}`, {
    x: 0.75,
    y: 3.5,
    w: 11,
    h: 0.45,
    fontFace: F_BODY,
    fontSize: 19,
    color: C.fg2,
    margin: 0,
  });
  s.addText(input.periodoLabel, {
    x: 0.75,
    y: 4.0,
    w: 11,
    h: 0.5,
    fontFace: F_TITLE,
    fontSize: 21,
    color: C.fg,
    margin: 0,
  });
  // Índice (contenido del deck, con conteos reales).
  const indice: string[] = [
    'Facturación',
    'Nivel de servicio',
    ...(input.incluirRrhh ? ['Indicadores RRHH (espacios reservados · 6)'] : []),
    `Productividad por área (${input.productividad.length})`,
    `Cumplimiento de líderes por línea (${input.lideres.length})`,
    `Paradas por línea y mes (${input.paros.length})`,
    'Análisis de costos',
  ];
  s.addText('CONTENIDO', {
    x: 0.75,
    y: 4.75,
    w: 5,
    h: 0.3,
    fontFace: F_BODY,
    fontSize: 10,
    color: C.fg3,
    charSpacing: 2,
    bold: true,
    margin: 0,
  });
  s.addText(indice.map((t, i) => `${String(i + 1).padStart(2, '0')}  ${t}`).join('\n'), {
    x: 0.75,
    y: 5.05,
    w: 7.5,
    h: indice.length * 0.24 + 0.2,
    fontFace: F_BODY,
    fontSize: 11,
    color: C.fg2,
    lineSpacing: 16,
    margin: 0,
  });
  if (input.incluirRrhh) {
    s.addText(
      'Las secciones marcadas con «espacio reservado» corresponden a información de origen externo a JIDOKAP (RRHH).\nTodo lo demás se genera automáticamente desde la plataforma con el rango de fechas seleccionado.',
      {
        x: 8.1,
        y: 5.05,
        w: 4.5,
        h: 1.1,
        fontFace: F_BODY,
        fontSize: 10,
        color: C.fg2,
        margin: 0,
      },
    );
  }
  s.addText(`Generado por ${input.generadoPor} desde JIDOKAP`, {
    x: 0.75,
    y: H - 0.75,
    w: 8,
    h: 0.3,
    fontFace: F_BODY,
    fontSize: 10,
    color: C.fg3,
    margin: 0,
  });
  footer(ctx, s);
}

function slideFacturacion(ctx: Ctx): void {
  const { pres, input } = ctx;
  const s = nuevaSlide(ctx);
  const f = input.facturacion;
  header(
    ctx,
    s,
    'Facturación',
    f
      ? `Millones COP · mes a mes por área · acumulado del período ${fmtM(f.acumuladoM)}`
      : 'Millones COP · mes a mes por área',
  );
  if (!f || f.meses.length === 0) {
    cajaVacia(
      s,
      'SIN DATOS DE FACTURACIÓN',
      'No hay OPs facturadas en el período seleccionado, o su usuario no tiene el permiso de costos.',
    );
    footer(ctx, s);
    return;
  }
  card(s, 0.55, 1.5, 8.5, 5.35);
  cardTitulo(s, 0.55, 1.5, 'Facturación mes a mes / área');
  const labels = f.meses.map((m) => mesCorto(m.label));
  const series = f.areas.map((area) => ({
    name: area,
    labels,
    values: f.meses.map((m) => m.porArea[area] ?? 0),
  }));
  const totales = f.meses.map((m) => m.totalM);
  const lineas: Array<{ name: string; labels: string[]; values: number[] }> = [
    // Serie invisible: pone el TOTAL del apilado como etiqueta arriba (truco
    // del mockup — pptxgenjs no etiqueta totales de stacked por sí solo).
    { name: 'Total mes', labels, values: totales },
  ];
  const coloresLinea: string[] = [C.white];
  if (f.metaMensualMillones !== null) {
    lineas.push({
      name: `Meta mensual ${fmtM(f.metaMensualMillones)}`,
      labels,
      values: labels.map(() => f.metaMensualMillones!),
    });
    coloresLinea.push(C.warn);
  }
  addComboChart(
    s,
    [
      {
        type: pres.ChartType.bar,
        data: series,
        options: co({
          barGrouping: 'stacked',
          chartColors: series.map((_, i) => PALETA_SERIES[i % PALETA_SERIES.length]!),
          showValue: true,
          dataLabelPosition: 'ctr', // NUNCA 'outEnd' en apilado (corrompe el XML)
          dataLabelColor: C.white,
          dataLabelFontSize: 8,
          dataLabelFontFace: F_BODY,
        }),
      },
      {
        type: pres.ChartType.line,
        data: lineas,
        options: co({
          chartColors: coloresLinea,
          lineSize: 2,
          lineDash: 'dash',
          lineDataSymbol: 'none',
          showValue: true,
          dataLabelPosition: 't',
          dataLabelColor: C.fg,
          dataLabelFontSize: 10.5,
          dataLabelFontFace: F_BODY,
          dataLabelFontBold: true,
          dataLabelFormatCode: '"$"#,##0.0;;;',
        }),
      },
    ],
    co({
      x: 0.75,
      y: 2.0,
      w: 8.1,
      h: 4.6,
      catAxisLabelColor: C.fg2,
      catAxisLabelFontFace: F_BODY,
      catAxisLabelFontSize: 11,
      valAxisLabelColor: C.fg3,
      valAxisLabelFontFace: F_BODY,
      valAxisLabelFontSize: 10,
      valGridLine: { color: C.borderSoft, size: 1 },
      catGridLine: { style: 'none' },
      showLegend: true,
      legendPos: 'b',
      legendColor: C.fg2,
      legendFontFace: F_BODY,
      legendFontSize: 10,
      showTitle: false,
    }),
  );
  kpiCard(
    s,
    9.3,
    1.5,
    3.45,
    'Acumulado del período',
    fmtM(f.acumuladoM),
    `${fmtMiles(f.ops)} OPs · ${fmtMiles(f.unidades)} unidades validadas`,
  );
  if (f.metaMensualMillones !== null) {
    const enMeta = f.meses.filter((m) => m.totalM >= f.metaMensualMillones!).length;
    kpiCard(
      s,
      9.3,
      2.95,
      3.45,
      'Meses en meta',
      `${enMeta} de ${f.meses.length}`,
      `meta mensual: ${fmtM(f.metaMensualMillones)}`,
      enMeta === f.meses.length ? C.ok : C.warn,
    );
  } else {
    kpiCard(
      s,
      9.3,
      2.95,
      3.45,
      'Meta mensual',
      '—',
      'sin meta en Configuración del Tenant (A1)',
      C.fg3,
    );
  }
  // Participación por área (barras horizontales, chart nativo editable).
  card(s, 9.3, 4.4, 3.45, 2.45);
  cardTitulo(s, 9.3, 4.4, 'Participación por área');
  const totArea = f.areas.map((a) => ({
    name: a,
    tot: f.meses.reduce((acc, m) => acc + (m.porArea[a] ?? 0), 0),
  }));
  const gran = totArea.reduce((acc, a) => acc + a.tot, 0) || 1;
  s.addChart(
    pres.ChartType.bar,
    [
      {
        name: 'Participación %',
        labels: totArea.map((a) => a.name).reverse(),
        values: totArea.map((a) => Math.round((a.tot / gran) * 1000) / 10).reverse(),
      },
    ],
    co({
      x: 9.42,
      y: 4.74,
      w: 3.22,
      h: 2.0,
      barDir: 'bar',
      chartColors: [C.accent],
      showValue: true,
      dataLabelPosition: 'outEnd',
      dataLabelColor: C.fg,
      dataLabelFontSize: 9,
      dataLabelFontFace: F_BODY,
      dataLabelFormatCode: '0.0"%"',
      catAxisLabelColor: C.fg2,
      catAxisLabelFontFace: F_BODY,
      catAxisLabelFontSize: 8.5,
      valAxisHidden: true,
      valGridLine: { style: 'none' },
      catGridLine: { style: 'none' },
      showLegend: false,
      showTitle: false,
    }),
  );
  s.addText(
    `${f.metaMensualMillones !== null ? `Línea punteada: meta mensual ${fmtM(f.metaMensualMillones)} · ` : ''}todos los gráficos son editables (clic derecho → Modificar datos).`,
    { x: 0.75, y: 6.55, w: 8.1, h: 0.26, fontFace: F_BODY, fontSize: 8.5, color: C.fg3, margin: 0 },
  );
  footer(ctx, s);
}

function slideNivel(ctx: Ctx): void {
  const { pres } = ctx;
  const s = nuevaSlide(ctx);
  const n: PptxNivel | null = ctx.input.nivel;
  header(
    ctx,
    s,
    'Niveles de servicio',
    'Unidades programadas vs realizadas por semana · cumplimiento ácido y real',
  );
  if (!n || n.semanas.length === 0) {
    cajaVacia(s, 'SIN DATOS DE NIVEL DE SERVICIO', 'No hay OPs programadas en el período.');
    footer(ctx, s);
    return;
  }
  card(s, 0.55, 1.5, 7.5, 5.35);
  cardTitulo(s, 0.55, 1.5, 'Nivel de servicio / semana');
  const labels = n.semanas.map((x) => x.label);
  addComboChart(
    s,
    [
      {
        type: pres.ChartType.bar,
        data: [
          { name: 'Programado', labels, values: n.semanas.map((x) => x.programadas) },
          { name: 'Realizado', labels, values: n.semanas.map((x) => x.realizadas) },
        ],
        options: co({
          chartColors: [C.accent, C.beige],
          showValue: true,
          dataLabelPosition: 'inEnd',
          dataLabelColor: C.white,
          dataLabelFontSize: 7.5,
          dataLabelFontFace: F_BODY,
          dataLabelFormatCode: '#,##0,"k"',
        }),
      },
      {
        type: pres.ChartType.line,
        data: [
          { name: 'Cumplimiento real', labels, values: n.semanas.map((x) => x.cumplReal ?? 0) },
          { name: 'Cumplimiento ácido', labels, values: n.semanas.map((x) => x.cumplAcido ?? 0) },
        ],
        options: co({
          chartColors: [C.ok, C.fg3],
          secondaryValAxis: true,
          secondaryCatAxis: true,
          lineSize: 2.5,
          lineSmooth: false,
          showValue: true,
          dataLabelPosition: 't',
          dataLabelColor: C.ok,
          dataLabelFontSize: 9,
          dataLabelFontFace: F_BODY,
          dataLabelFormatCode: '0"%"',
        }),
      },
    ],
    co({
      x: 0.72,
      y: 2.0,
      w: 7.15,
      h: 4.35,
      // Combo con eje secundario: SIEMPRE 2 entradas en valAxes y catAxes.
      valAxes: [
        {
          showValAxisTitle: false,
          valAxisLabelColor: C.fg3,
          valAxisLabelFontFace: F_BODY,
          valAxisLabelFontSize: 9,
          valGridLine: { color: C.borderSoft, size: 1 },
        },
        {
          showValAxisTitle: false,
          valAxisLabelColor: C.ok,
          valAxisLabelFontFace: F_BODY,
          valAxisLabelFontSize: 9,
          valAxisMinVal: 0,
          valAxisMaxVal: 120,
          valGridLine: { style: 'none' },
        },
      ],
      catAxes: [
        { catAxisLabelColor: C.fg2, catAxisLabelFontFace: F_BODY, catAxisLabelFontSize: 9 },
        { catAxisHidden: true },
      ],
      showLegend: true,
      legendPos: 'b',
      legendColor: C.fg2,
      legendFontFace: F_BODY,
      legendFontSize: 9,
      showTitle: false,
    }),
  );
  s.addText(
    `Acumulado del período: real ${fmtPct(n.acumulado.cumplReal)} · ácido ${fmtPct(n.acumulado.cumplAcido)} · ${fmtMiles(n.acumulado.real)} unds realizadas de ${fmtMiles(n.acumulado.prog)} programadas. Semanas etiquetadas por su fecha de inicio.`,
    { x: 0.75, y: 6.48, w: 7.1, h: 0.34, fontFace: F_BODY, fontSize: 8.5, color: C.fg3, margin: 0 },
  );

  // Tabla área × semana (cumplimiento REAL) con fila Total.
  card(s, 8.2, 1.5, 4.58, 5.35);
  cardTitulo(s, 8.2, 1.5, 'Cumplimiento real por área × semana');
  const areas = n.areas.slice(0, 4);
  const pctCell = (v: number | null, bold = false): Cell =>
    v === null
      ? cel('—', { color: C.fg3 })
      : cel(`${Math.round(v)}%`, { color: semColor(v), bold });
  const filas: Cell[][] = [
    [enc('Sem.', 'left'), ...areas.map((a) => enc(a.slice(0, 9))), enc('Planta')],
    ...n.semanas.map((sem, si) => [
      cel(sem.label.replace('Sem. del ', ''), { color: C.fg2, align: 'left' }),
      ...areas.map((_, ai) => pctCell(n.matriz[n.areas.indexOf(areas[ai]!)]?.[si] ?? null)),
      pctCell(sem.cumplReal, true),
    ]),
  ];
  const promArea = (ai: number): number | null => {
    const vals = (n.matriz[n.areas.indexOf(areas[ai]!)] ?? []).filter(
      (v): v is number => v !== null,
    );
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  filas.push([
    cel('Total', { color: C.fg, bold: true, align: 'left' }),
    ...areas.map((_, ai) => pctCell(promArea(ai), true)),
    pctCell(n.acumulado.cumplReal, true),
  ]);
  const anchoCol = (4.28 - 0.72 - 0.58) / Math.max(1, areas.length);
  s.addTable(filas, {
    x: 8.36,
    y: 2.0,
    w: 4.28,
    colW: [0.72, ...areas.map(() => anchoCol), 0.58],
    fontSize: 8.5,
    rowH: 0.38,
    valign: 'middle',
    border: { type: 'solid', color: C.borderSoft, pt: 0.75 },
  });
  s.addText('Verde ≥ 90 · ámbar ≥ 70 · rojo < 70 · tabla editable', {
    x: 8.36,
    y: 6.5,
    w: 4.2,
    h: 0.26,
    fontFace: F_BODY,
    fontSize: 8,
    color: C.fg3,
    margin: 0,
  });
  footer(ctx, s);
}

const RRHH_SLIDES: ReadonlyArray<[string, string]> = [
  ['Indicador de rotación de personal', 'Ingresos, retiros y % de rotación mensual'],
  ['Indicador de accidentalidad', 'Accidentes de trabajo por mes y área'],
  ['Detalle de accidentalidad', 'Descripción de eventos y días perdidos'],
  ['Días de incapacidad por enfermedad general', 'Días de incapacidad por mes'],
  ['Indicador de ausentismo por enfermedad general', '% de ausentismo mensual'],
  ['Ausentismo — detalle por diagnóstico médico', 'Distribución por diagnóstico'],
];

function slidesRrhh(ctx: Ctx): void {
  for (const [t, d] of RRHH_SLIDES) {
    const s = nuevaSlide(ctx);
    header(ctx, s, t, d);
    cajaVacia(
      s,
      'ESPACIO RESERVADO',
      'Información de origen externo a JIDOKAP (RRHH).\nEsta sección se completa manualmente con la fuente actual.',
    );
    footer(ctx, s, false);
  }
}

function slideProductividad(
  ctx: Ctx,
  areaNombre: string,
  mesLabel: string,
  d: PptxProductividadCelda | null,
): void {
  const { pres } = ctx;
  const s = nuevaSlide(ctx);
  header(
    ctx,
    s,
    `Productividad ${areaNombre} — ${mesLabel}`,
    'Perfil por hora · producción por turno · detalle por línea',
  );
  if (!d || d.producido <= 0) {
    cajaVacia(
      s,
      'SIN DATOS DEL PERÍODO',
      `Sin producción capturada en ${areaNombre} durante ${mesLabel}.`,
    );
    footer(ctx, s);
    return;
  }

  // Fila 1: perfil por hora (combo) + KPIs 2×2.
  card(s, 0.55, 1.5, 7.5, 2.78);
  cardTitulo(s, 0.55, 1.5, 'Perfil por hora');
  const horasLbl = d.porHora.map((h) => h.label);
  addComboChart(
    s,
    [
      {
        type: pres.ChartType.bar,
        data: [
          {
            name: 'Cantidad producida',
            labels: horasLbl,
            values: d.porHora.map((h) => h.cantidad),
          },
        ],
        options: co({
          chartColors: [C.azul],
          showValue: true,
          dataLabelPosition: 'outEnd',
          dataLabelColor: C.fg2,
          dataLabelFontSize: 5.5,
          dataLabelFontFace: F_BODY,
          dataLabelFormatCode: '#,##0,"k"',
        }),
      },
      {
        type: pres.ChartType.line,
        data: [
          {
            name: 'Cumplimiento vs. meta',
            labels: horasLbl,
            values: d.porHora.map((h) => h.cumplimiento ?? 0),
          },
        ],
        options: co({
          chartColors: [C.ok],
          secondaryValAxis: true,
          secondaryCatAxis: true,
          lineSize: 1.75,
          lineSmooth: true,
        }),
      },
    ],
    co({
      x: 0.68,
      y: 1.88,
      w: 7.25,
      h: 2.3,
      valAxes: [
        {
          showValAxisTitle: false,
          valAxisLabelColor: C.fg3,
          valAxisLabelFontFace: F_BODY,
          valAxisLabelFontSize: 7.5,
          valGridLine: { color: C.borderSoft, size: 1 },
        },
        {
          showValAxisTitle: false,
          valAxisLabelColor: C.ok,
          valAxisLabelFontFace: F_BODY,
          valAxisLabelFontSize: 7.5,
          valAxisMinVal: 0,
          valAxisMaxVal: 130,
          valGridLine: { style: 'none' },
        },
      ],
      catAxes: [
        {
          catAxisLabelColor: C.fg2,
          catAxisLabelFontFace: F_BODY,
          catAxisLabelFontSize: 7,
          catAxisLabelFrequency: 3,
        },
        { catAxisHidden: true },
      ],
      showLegend: true,
      legendPos: 'b',
      legendColor: C.fg2,
      legendFontFace: F_BODY,
      legendFontSize: 8,
      showTitle: false,
    }),
  );
  kpiCard(
    s,
    8.2,
    1.5,
    2.24,
    'Producido',
    fmtMiles(d.producido),
    `unidades · ${mesLabel.toLowerCase()}`,
  );
  kpiCard(
    s,
    10.54,
    1.5,
    2.24,
    'Cumpl. meta',
    fmtPct(d.cumplPct),
    'promedio del mes',
    semColor(d.cumplPct),
  );
  kpiCard(
    s,
    8.2,
    3.0,
    2.24,
    'Horas de paro',
    `${d.horasParo.toLocaleString('es-CO')} h`,
    'del mes en el área',
  );
  kpiCard(
    s,
    10.54,
    3.0,
    2.24,
    'Utilidad área',
    d.utilidadM === null ? '—' : fmtM(d.utilidadM),
    d.utilidadM === null ? 'requiere permiso de costos' : 'valor − costo MO',
    d.utilidadM === null ? C.fg3 : C.ok,
  );

  // Fila 2: producción por turno + detalle por línea.
  card(s, 0.55, 4.45, 3.95, 2.6);
  cardTitulo(s, 0.55, 4.45, 'Producción por turno');
  if (d.porTurno.length > 0) {
    const turnosLbl = d.porTurno.map((t) => t.label);
    addComboChart(
      s,
      [
        {
          type: pres.ChartType.bar,
          data: [
            {
              name: 'Cantidad producida',
              labels: turnosLbl,
              values: d.porTurno.map((t) => t.cantidad),
            },
          ],
          options: co({
            chartColors: [C.azul],
            showValue: true,
            dataLabelPosition: 'outEnd',
            dataLabelColor: C.fg2,
            dataLabelFontSize: 8,
            dataLabelFontFace: F_BODY,
            dataLabelFormatCode: '#,##0',
          }),
        },
        {
          type: pres.ChartType.line,
          data: [
            {
              name: 'Cumplimiento vs. meta',
              labels: turnosLbl,
              values: d.porTurno.map((t) => t.cumplimiento ?? 0),
            },
          ],
          options: co({
            chartColors: [C.ok],
            secondaryValAxis: true,
            secondaryCatAxis: true,
            lineSize: 2,
            lineSmooth: false,
            showValue: true,
            dataLabelPosition: 't',
            dataLabelColor: C.ok,
            dataLabelFontSize: 8.5,
            dataLabelFontFace: F_BODY,
            dataLabelFormatCode: '0"%"',
          }),
        },
      ],
      co({
        x: 0.66,
        y: 4.85,
        w: 3.7,
        h: 2.05,
        valAxes: [
          {
            showValAxisTitle: false,
            valAxisLabelColor: C.fg3,
            valAxisLabelFontFace: F_BODY,
            valAxisLabelFontSize: 7.5,
            valGridLine: { color: C.borderSoft, size: 1 },
          },
          {
            showValAxisTitle: false,
            valAxisHidden: true,
            valAxisMinVal: 0,
            valAxisMaxVal: 130,
            valGridLine: { style: 'none' },
          },
        ],
        catAxes: [
          { catAxisLabelColor: C.fg2, catAxisLabelFontFace: F_BODY, catAxisLabelFontSize: 9 },
          { catAxisHidden: true },
        ],
        showLegend: false,
        showTitle: false,
      }),
    );
  }

  card(s, 4.65, 4.45, 8.13, 2.6);
  cardTitulo(s, 4.65, 4.45, 'Detalle por línea');
  const lineas = d.lineas.slice(0, 4);
  const tp = d.lineas.reduce((a, f) => a + f.producido, 0);
  const tm = d.lineas.reduce((a, f) => a + f.meta, 0);
  const pctTot = tm > 0 ? Math.round((tp / tm) * 100) : null;
  const tabla: Cell[][] = [
    [
      enc('Línea', 'left'),
      enc('Producido'),
      enc('Meta'),
      enc('% Meta'),
      enc('H. trab.'),
      enc('H. paro'),
      enc('Top causal', 'left'),
    ],
    ...lineas.map((f) => [
      cel(f.codigo, { align: 'left', bold: true, color: C.fg }),
      cel(fmtMiles(f.producido), { color: C.fg2 }),
      cel(fmtMiles(f.meta), { color: C.fg3 }),
      cel(fmtPct(f.pct), { color: semColor(f.pct), bold: true }),
      cel(`${Math.round(f.horasTrab)} h`, { color: C.fg2 }),
      cel(`${f.horasParo.toLocaleString('es-CO')} h`, { color: C.fg2 }),
      cel(trunca(f.topCausal ?? '—', 26), { align: 'left', color: C.fg2 }),
    ]),
    [
      cel('Total', { align: 'left', bold: true, color: C.fg }),
      cel(fmtMiles(tp), { bold: true, color: C.fg }),
      cel(fmtMiles(tm), { bold: true, color: C.fg }),
      cel(fmtPct(pctTot), { color: semColor(pctTot), bold: true }),
      cel(`${Math.round(d.lineas.reduce((a, f) => a + f.horasTrab, 0))} h`, {
        bold: true,
        color: C.fg,
      }),
      cel(
        `${d.lineas.reduce((a, f) => a + f.horasParo, 0).toLocaleString('es-CO', { maximumFractionDigits: 1 })} h`,
        { bold: true, color: C.fg },
      ),
      cel('', {}),
    ],
  ];
  s.addTable(tabla, {
    x: 4.8,
    y: 4.85,
    w: 7.85,
    colW: [0.85, 1.15, 1.15, 0.8, 0.95, 0.95, 2.0],
    fontSize: 9,
    rowH: 0.38,
    valign: 'middle',
    border: { type: 'solid', color: C.borderSoft, pt: 0.75 },
  });
  footer(ctx, s);
}

function slideLideres(ctx: Ctx, linea: PptxLideresLinea): void {
  const s = nuevaSlide(ctx);
  header(
    ctx,
    s,
    `Cumplimiento líderes — ${linea.lineaLabel}`,
    'Producido vs esperado por líder · solo producción validada',
  );
  const meses = linea.meses;
  const anchoCard = meses.length > 1 ? 6.0 : 8.0;
  const posX = meses.length > 1 ? [0.55, 6.78] : [(W - anchoCard) / 2];

  const mkTabla = (m: NonNullable<(typeof meses)[number]>): Cell[][] => {
    const top = m.lideres.slice(0, 8);
    const tp = m.lideres.reduce((a, r) => a + r.producido, 0);
    const te = m.lideres.reduce((a, r) => a + r.esperado, 0);
    const pctTot = te > 0 ? Math.round((tp / te) * 100) : null;
    return [
      [
        enc('#', 'left'),
        enc('Líder', 'left'),
        enc('Producido'),
        enc('Esperado'),
        enc('Cumpl.'),
        enc('Horas'),
        enc('Días'),
      ],
      ...top.map((r, i) => [
        cel(i + 1, { align: 'left', color: C.fg3 }),
        cel(trunca(r.nombre, 19), { align: 'left', bold: true, color: C.fg }),
        cel(fmtMiles(r.producido), { color: C.fg2 }),
        cel(fmtMiles(r.esperado), { color: C.fg3 }),
        cel(fmtPct(r.pct), { color: semColor(r.pct), bold: true }),
        cel(r.horas, { color: C.fg2 }),
        cel(r.dias, { color: C.fg2 }),
      ]),
      [
        cel('', {}),
        cel(`Total (${m.lideres.length} líderes)`, { align: 'left', bold: true, color: C.fg }),
        cel(fmtMiles(tp), { bold: true, color: C.fg }),
        cel(fmtMiles(te), { bold: true, color: C.fg }),
        cel(fmtPct(pctTot), { color: semColor(pctTot), bold: true }),
        cel(
          m.lideres.reduce((a, r) => a + r.horas, 0),
          { bold: true, color: C.fg },
        ),
        cel('—', { color: C.fg3 }),
      ],
    ];
  };

  meses.forEach((m, i) => {
    const x = posX[i]!;
    card(s, x, 1.5, anchoCard, 5.0);
    if (!m || m.lideres.length === 0) {
      cardTitulo(s, x, 1.5, m?.mesLabel ?? 'Sin datos');
      s.addText('Sin producción capturada en la línea este mes.', {
        x: x + 0.3,
        y: 3.6,
        w: anchoCard - 0.6,
        h: 0.4,
        align: 'center',
        fontFace: F_BODY,
        fontSize: 11,
        color: C.fg3,
        margin: 0,
      });
      return;
    }
    // Con más de 8 líderes la tabla recorta: decirlo (el Total sí incluye a todos).
    cardTitulo(
      s,
      x,
      1.5,
      m.lideres.length > 8 ? `${m.mesLabel} · top 8 de ${m.lideres.length}` : m.mesLabel,
    );
    s.addTable(mkTabla(m), {
      x: x + 0.2,
      y: 1.98,
      w: 5.6,
      colW: [0.3, 1.62, 0.92, 0.92, 0.68, 0.6, 0.56],
      fontSize: 9,
      rowH: 0.44,
      valign: 'middle',
      border: { type: 'solid', color: C.borderSoft, pt: 0.75 },
    });
  });

  // Insight calculado mes anterior → último (solo si hay dos meses con datos).
  const [mA, mB] = [meses[0], meses[1]];
  if (meses.length === 2 && mA && mB && mA.lideres.length > 0 && mB.lideres.length > 0) {
    const deltas = mB.lideres
      .map((r) => {
        const antes = mA.lideres.find((x) => x.nombre === r.nombre);
        return antes && antes.pct !== null && r.pct !== null
          ? { nom: r.nombre, d: Math.round(r.pct - antes.pct) }
          : null;
      })
      .filter((x): x is { nom: string; d: number } => x !== null);
    if (deltas.length > 0) {
      const mejor = deltas.reduce((a, b) => (b.d > a.d ? b : a));
      const peor = deltas.reduce((a, b) => (b.d < a.d ? b : a));
      s.addText(
        [
          { text: `${mA.mesLabel} → ${mB.mesLabel.toLowerCase()}:  `, options: { color: C.fg3 } },
          {
            text: `mayor avance ${mejor.nom} (+${mejor.d} pp)`,
            options: { color: C.ok, bold: true },
          },
          { text: '   ·   ', options: { color: C.fg3 } },
          {
            text: `mayor caída ${peor.nom} (${peor.d} pp)`,
            options: { color: C.crit, bold: true },
          },
          {
            text: '   ·   % = producido / esperado según capacidad y horas asignadas',
            options: { color: C.fg3 },
          },
        ],
        { x: 0.55, y: 6.62, w: 12.2, h: 0.3, fontFace: F_BODY, fontSize: 9.5, margin: 0 },
      );
    }
  }
  footer(ctx, s);
}

function slideParos(
  ctx: Ctx,
  lineaLabel: string,
  mesLabel: string,
  d: PptxParosCelda | null,
): void {
  const { pres } = ctx;
  const s = nuevaSlide(ctx);
  header(
    ctx,
    s,
    `Paradas ${lineaLabel} — ${mesLabel}`,
    'Tiempo perdido por categoría y causal específica · eventos, % del total y pérdida $',
  );
  if (!d || d.categorias.length === 0) {
    cajaVacia(s, 'SIN PARADAS REGISTRADAS', `La línea no registró paros cerrados en ${mesLabel}.`);
    footer(ctx, s);
    return;
  }
  const cats = d.categorias.slice(0, 4).map((cat, i) => ({
    ...cat,
    color: cat.color?.replace('#', '') ?? PALETA_SERIES[i % PALETA_SERIES.length]!,
    causales: cat.causales.slice(0, 4),
  }));

  card(s, 0.55, 1.5, 7.95, 5.35);
  cardTitulo(s, 0.55, 1.5, 'Causal específica por categoría');
  const filasT: Cell[][] = [
    [enc('Causal', 'left'), enc('# Eventos'), enc('Tiempo'), enc('% total'), enc('Pérdida $')],
  ];
  for (const cat of cats) {
    filasT.push([
      cel(cat.nombre.toUpperCase(), {
        align: 'left',
        colspan: 5,
        color: cat.color,
        bold: true,
        fill: { color: C.bgSoft },
        fontSize: 8,
      }),
    ]);
    for (const f of cat.causales) {
      filasT.push([
        cel(f.nombre, { align: 'left', color: C.fg }),
        cel(f.eventos, { color: C.accent }),
        cel(fmtTiempo(f.minutos), { color: C.fg }),
        cel(`${f.pct.toLocaleString('es-CO', { maximumFractionDigits: 1 })}%`, { color: C.fg2 }),
        cel(f.perdida === null ? '—' : fmtM(f.perdida / 1_000_000), {
          color: f.perdida === null ? C.fg3 : C.fg,
        }),
      ]);
    }
  }
  filasT.push([
    cel(`Total (${cats.reduce((a, c) => a + c.causales.length, 0)} causales)`, {
      align: 'left',
      bold: true,
      color: C.fg,
    }),
    cel(d.totalEv, { bold: true, color: C.fg }),
    cel(fmtTiempo(d.totalMin), { bold: true, color: C.fg }),
    cel('100%', { bold: true, color: C.fg }),
    cel(d.totalPerdida === null ? '—' : fmtM(d.totalPerdida / 1_000_000), {
      bold: true,
      color: d.totalPerdida === null ? C.fg3 : C.fg,
    }),
  ]);
  s.addTable(filasT, {
    x: 0.72,
    y: 1.95,
    w: 7.6,
    colW: [3.35, 0.9, 1.0, 0.85, 1.5],
    fontSize: 8.5,
    rowH: 0.32,
    valign: 'middle',
    border: { type: 'solid', color: C.borderSoft, pt: 0.75 },
  });
  s.addText(
    'Pérdida $ = unidades no producidas × precio SKU. Los paros programados no descuentan disponibilidad: su desempeño se mide contra meta en «Entrada a Pits».',
    { x: 0.72, y: 6.52, w: 7.6, h: 0.32, fontFace: F_BODY, fontSize: 8, color: C.fg3, margin: 0 },
  );

  kpiCard(
    s,
    8.65,
    1.5,
    4.1,
    'Paro total registrado',
    fmtTiempo(d.totalMin),
    `${d.totalEv} eventos cerrados · incluye programados · ${mesLabel.toLowerCase()}`,
  );
  kpiCard(
    s,
    8.65,
    2.95,
    4.1,
    'Pérdida por paros',
    d.totalPerdida === null ? '—' : fmtM(d.totalPerdida / 1_000_000),
    'valor no producido · solo paros que afectan disponibilidad',
    d.totalPerdida === null ? C.fg3 : C.fg,
  );
  card(s, 8.65, 4.4, 4.1, 2.45);
  cardTitulo(s, 8.65, 4.4, 'Distribución por categoría');
  const totMinSeguro = d.totalMin || 1;
  s.addChart(
    pres.ChartType.doughnut,
    [
      {
        name: 'Minutos por categoría',
        labels: cats.map((c) => c.nombre),
        values: cats.map(
          (c) =>
            Math.round((c.causales.reduce((x, f) => x + f.minutos, 0) / totMinSeguro) * 1000) / 10,
        ),
      },
    ],
    co({
      x: 8.78,
      y: 4.74,
      w: 3.85,
      h: 2.0,
      holeSize: 55,
      chartColors: cats.map((c) => c.color), // doughnut: un color POR PUNTO
      showPercent: true,
      dataLabelColor: C.white,
      dataLabelFontSize: 9,
      dataLabelFontFace: F_BODY,
      showLegend: true,
      legendPos: 'r',
      legendColor: C.fg2,
      legendFontFace: F_BODY,
      legendFontSize: 8,
      showTitle: false,
    }),
  );
  footer(ctx, s);
}

function slideCostos(ctx: Ctx): void {
  const { pres, input } = ctx;
  const s = nuevaSlide(ctx);
  const c = input.costos;
  header(
    ctx,
    s,
    'Análisis de costos',
    'Valor producido, costo de mano de obra y utilidad estimada del período',
  );
  if (!c) {
    cajaVacia(
      s,
      'SIN DATOS DE COSTOS',
      'Verifique el permiso «puede ver costos» y los precios cargados en C10.',
    );
    footer(ctx, s);
    return;
  }
  kpiCard(
    s,
    0.55,
    1.6,
    3.0,
    'Valor producido',
    c.valorM === null ? '—' : fmtM(c.valorM),
    `precio vigente × paquetes · ${c.baseLabel ?? 'producción capturada'}`,
  );
  kpiCard(
    s,
    3.75,
    1.6,
    3.0,
    'Costo mano de obra',
    c.costoMoM === null ? '—' : fmtM(c.costoMoM),
    'operarios × horas reales × hora-hombre vigente',
  );
  kpiCard(
    s,
    6.95,
    1.6,
    3.0,
    'Utilidad estimada',
    c.utilidadM === null ? '—' : fmtM(c.utilidadM),
    'valor producido − costo MO',
    c.utilidadM !== null && c.utilidadM < 0 ? C.crit : C.ok,
  );
  kpiCard(
    s,
    10.15,
    1.6,
    2.6,
    'Pérdida por paros',
    c.perdidaParosM === null ? '—' : fmtM(c.perdidaParosM),
    'valor no producido',
    c.perdidaParosM === null ? C.fg3 : C.fg,
  );

  if (c.porArea.length > 0) {
    card(s, 0.55, 3.2, 6.4, 3.65);
    cardTitulo(s, 0.55, 3.2, 'Utilidad por área (millones COP)');
    const ordenado = [...c.porArea].sort((a, b) => b.utilidadM - a.utilidadM);
    // La tabla muestra hasta 6 filas; si hay más, el resto va en 'Otras (N)' para
    // que el total de la tabla siempre concilie con lo mostrado.
    const filasTabla = filasConOtras(
      ordenado.map((r) => ({
        nombre: r.nombre,
        valor_producido: r.valorM,
        costo_mano_obra: r.moM,
        utilidad: r.utilidadM,
        utilidad_pct: r.valorM > 0 ? (r.utilidadM / r.valorM) * 100 : null,
      })),
      6,
    );
    s.addChart(
      pres.ChartType.bar,
      [
        {
          name: 'Utilidad',
          labels: ordenado.map((a) => a.nombre),
          values: ordenado.map((a) => a.utilidadM),
        },
      ],
      co({
        x: 0.7,
        y: 3.62,
        w: 6.1,
        h: 3.05,
        chartColors: [C.accent],
        showValue: true,
        dataLabelPosition: 'outEnd',
        dataLabelColor: C.fg2,
        dataLabelFontSize: 9,
        dataLabelFontFace: F_BODY,
        dataLabelFormatCode: '"$"#,##0.0',
        catAxisLabelColor: C.fg2,
        catAxisLabelFontFace: F_BODY,
        catAxisLabelFontSize: 10,
        valAxisLabelColor: C.fg3,
        valAxisLabelFontFace: F_BODY,
        valAxisLabelFontSize: 9,
        valGridLine: { color: C.borderSoft, size: 1 },
        catGridLine: { style: 'none' },
        showLegend: false,
        showTitle: false,
      }),
    );

    card(s, 7.15, 3.2, 5.6, 3.65);
    cardTitulo(s, 7.15, 3.2, 'Detalle por área · millones COP');
    const filasC: Cell[][] = [
      [enc('Área', 'left'), enc('Valor prod.'), enc('Costo MO'), enc('Utilidad'), enc('Margen')],
      ...filasTabla.map((r) => {
        const mg = r.utilidad_pct === null ? null : Math.round(r.utilidad_pct);
        return [
          cel(trunca(r.nombre, 19), { align: 'left', bold: true, color: C.fg }),
          cel(fmtM(r.valor_producido), { color: C.fg2 }),
          cel(fmtM(r.costo_mano_obra), { color: C.fg2 }),
          cel(fmtM(r.utilidad), { color: r.utilidad >= 0 ? C.ok : C.crit, bold: true }),
          cel(mg === null ? '—' : `${mg}%`, {
            color: mg === null ? C.fg3 : mg >= 20 ? C.ok : mg >= 10 ? C.warn : C.crit,
          }),
        ];
      }),
    ];
    // Total = el mismo KPI de arriba (resumenCostosInforme garantiza que Σ filas
    // = total; acá se imprime la misma cifra para que tabla y tarjeta coincidan).
    const tv = c.valorM ?? ordenado.reduce((a, r) => a + r.valorM, 0);
    const tmo = c.costoMoM ?? ordenado.reduce((a, r) => a + r.moM, 0);
    const tut = c.utilidadM ?? ordenado.reduce((a, r) => a + r.utilidadM, 0);
    filasC.push([
      cel('Total', { align: 'left', bold: true, color: C.fg }),
      cel(fmtM(tv), { bold: true, color: C.fg }),
      cel(fmtM(tmo), { bold: true, color: C.fg }),
      cel(fmtM(tut), { bold: true, color: tut >= 0 ? C.ok : C.crit }),
      cel(tv > 0 ? `${Math.round((tut / tv) * 100)}%` : '—', { bold: true, color: C.fg }),
    ]);
    s.addTable(filasC, {
      x: 7.3,
      y: 3.62,
      w: 5.3,
      colW: [1.15, 1.1, 1.05, 1.05, 0.95],
      fontSize: 8.5,
      rowH: 0.38,
      valign: 'middle',
      border: { type: 'solid', color: C.borderSoft, pt: 0.75 },
    });
    s.addText(
      c.notaBase ??
        `Utilidad = valor producido − costo MO, solo producto con tarifa · líneas + tiendas · ${c.baseLabel ?? 'producción capturada'}.`,
      { x: 0.55, y: 6.9, w: 12.2, h: 0.28, fontFace: F_BODY, fontSize: 7, color: C.fg3, margin: 0 },
    );
    s.addText(
      c.notaExclusiones ??
        (c.sinTarifa
          ? `Sin tarifa, fuera del cálculo: ${fmtMiles(c.sinTarifa.paquetes)} paq de ${c.sinTarifa.skus} SKU(s), MO sin asignar ${fmtM(c.sinTarifa.costoMoM)}.`
          : 'Toda la producción del período tiene tarifa.'),
      { x: 7.3, y: 6.5, w: 5.3, h: 0.36, fontFace: F_BODY, fontSize: 7, color: C.fg3, margin: 0 },
    );
  }
  footer(ctx, s);
}

function slideCierre(ctx: Ctx): void {
  const { input } = ctx;
  const s = nuevaSlide(ctx);
  s.background = { color: C.bgSoft };
  s.addText('Gracias', {
    x: 0.75,
    y: 2.9,
    w: 11.8,
    h: 1.1,
    align: 'center',
    fontFace: F_TITLE,
    fontSize: 54,
    bold: true,
    color: C.fg,
    margin: 0,
  });
  s.addText(
    [
      { text: 'Jidokap', options: { color: C.fg, fontFace: F_TITLE } },
      { text: '.', options: { color: C.accent, fontFace: F_TITLE } },
      { text: '  ×  ', options: { color: C.fg3 } },
      { text: input.tenantNombre, options: { color: C.fg2, bold: true } },
      {
        text: '   ·   informe generado desde la plataforma',
        options: { color: C.fg3, fontSize: 12 },
      },
    ],
    {
      x: 0.75,
      y: 4.1,
      w: 11.8,
      h: 0.5,
      align: 'center',
      fontFace: F_BODY,
      fontSize: 16,
      margin: 0,
    },
  );
  footer(ctx, s, true);
}

// =============================================================================
// Entradas públicas
// =============================================================================

/** Slides que produce el deck para este input (portada y cierre incluidos). */
export function contarSlides(input: PptxInput): number {
  return (
    5 + // portada + facturación + nivel + costos + cierre
    (input.incluirRrhh ? RRHH_SLIDES.length : 0) +
    input.productividad.length +
    input.lideres.length +
    input.paros.length
  );
}

/**
 * Construye el deck (pptxgenjs cargado con import() dinámico). Devuelve la
 * instancia y el total de slides — el smoke test la serializa a base64.
 */
export async function construirPptx(
  input: PptxInput,
  onProgreso?: (msg: string) => void,
): Promise<{ pptx: PptxGenJS; totalSlides: number }> {
  let PptxCtor: typeof PptxGenJS;
  try {
    ({ default: PptxCtor } = await import('pptxgenjs'));
  } catch {
    // Módulo con hash que ya no existe en el servidor (versión nueva desplegada
    // con la pestaña abierta). main.tsx recarga una vez; si aun así llega acá,
    // el mensaje le dice a la persona qué hacer en vez del error crudo del navegador.
    throw new Error(
      'Hay una versión nueva de la aplicación. Recargue la página e intente descargar de nuevo.',
    );
  }
  const pres = new PptxCtor();
  pres.layout = 'LAYOUT_WIDE'; // 13.33 × 7.5
  const ctx: Ctx = { pres, input, total: contarSlides(input), pageNum: 0, onProgreso };

  slidePortada(ctx);
  slideFacturacion(ctx);
  slideNivel(ctx);
  if (input.incluirRrhh) slidesRrhh(ctx);
  for (const slot of input.productividad) {
    slideProductividad(ctx, slot.areaNombre, slot.mesLabel, slot.datos);
  }
  for (const linea of input.lideres) slideLideres(ctx, linea);
  for (const slot of input.paros) slideParos(ctx, slot.lineaLabel, slot.mesLabel, slot.datos);
  slideCostos(ctx);
  slideCierre(ctx);

  return { pptx: pres, totalSlides: ctx.pageNum };
}

/** Genera y descarga el .pptx en el browser (writeFile dispara la descarga). */
export async function descargarInformeGerencialPptx(
  input: PptxInput,
  onProgreso?: (msg: string) => void,
): Promise<void> {
  const { pptx } = await construirPptx(input, onProgreso);
  onProgreso?.('Escribiendo el archivo…');
  const nombre = `informe-gerencial-${input.periodoLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pptx`;
  await pptx.writeFile({ fileName: nombre });
}
